import { APP_VERSION, isNativeShell } from "../config/appConfig";
import { getDeviceId } from "../lib/deviceStorage";
import { BOOT_TELEMETRY_DEBUG_KEY, queueBootTelemetryRecord } from "./bootTelemetryReporter";


export function ensureBootTelemetryState() {
  try {
    if (typeof window === "undefined") return null;
    if (window.__fzBootTelemetry) return window.__fzBootTelemetry;
    const perfNow = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : 0;
    const nav = typeof navigator !== "undefined" ? navigator : null;
    const conn = nav?.connection || nav?.mozConnection || nav?.webkitConnection || null;
    const state = {
      bootId: `boot_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      startedAt: Date.now(),
      startedPerf: perfNow,
      finalized: false,
      phases: [{ name: "app-start", atMs: 0, atIso: new Date().toISOString(), details: {} }],
      identity: {
        accountId: "",
        userId: "",
        username: "",
        userName: "",
        role: "",
      },
      farm: { id: "", name: "" },
      context: {
        route: String(window.location?.pathname || ""),
        query: String(window.location?.search || ""),
        appVersion: String(window.APP_VERSION || APP_VERSION || ""),
        isNativeShell: isNativeShell(),
        platform: String(nav?.platform || ""),
        userAgent: String(nav?.userAgent || ""),
        language: String(nav?.language || ""),
        visibilityState: typeof document !== "undefined" ? String(document.visibilityState || "") : "",
        connectionType: String(conn?.type || ""),
        effectiveType: String(conn?.effectiveType || ""),
        saveData: !!conn?.saveData,
        downlink: Number(conn?.downlink || 0) || 0,
        rtt: Number(conn?.rtt || 0) || 0,
        deviceMemory: Number(nav?.deviceMemory || 0) || 0,
        hardwareConcurrency: Number(nav?.hardwareConcurrency || 0) || 0,
      },
      marks: {},
      summary: {},
    };
    window.__fzBootTelemetry = state;
    window.__fzBootTelemetryDebug = state;
    return state;
  } catch (e) {
    return null;
  }
}

export function markBootTelemetry(name, details = {}) {
  try {
    const state = ensureBootTelemetryState();
    if (!state || !name) return null;
    const perfNow = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : 0;
    const atMs = Math.max(0, Math.round(perfNow - Number(state.startedPerf || 0)));
    const entry = { name: String(name), atMs, atIso: new Date().toISOString(), details: details && typeof details === "object" ? details : {} };
    state.phases = Array.isArray(state.phases) ? state.phases : [];
    state.phases.push(entry);
    state.marks[String(name)] = entry;
    state.context = { ...(state.context || {}), visibilityState: typeof document !== "undefined" ? String(document.visibilityState || "") : String(state.context?.visibilityState || "") };
    try {
      localStorage.setItem(
        BOOT_TELEMETRY_DEBUG_KEY,
        JSON.stringify({
          bootId: state.bootId,
          phases: state.phases.slice(-80),
          summary: state.summary || {},
          identity: state.identity || {},
          farm: state.farm || {},
          context: state.context || {},
        }),
      );
    } catch (e) {}
    return entry;
  } catch (e) {
    return null;
  }
}

export function setBootTelemetryIdentity(session, farm = null) {
  try {
    const state = ensureBootTelemetryState();
    if (!state) return;
    if (session) {
      state.identity = {
        accountId: String(session?.id || "").trim(),
        userId: String(session?.userId || "").trim(),
        username: String(session?.username || "").trim(),
        userName: String(session?.name || session?.username || "").trim(),
        role: String(session?.role || "").trim(),
      };
    }
    if (farm !== undefined && farm !== null) {
      state.farm = {
        id: String(farm?.id || "").trim(),
        name: String(farm?.name || "").trim(),
      };
    }
  } catch (e) {}
}

export function buildBootTelemetryDurations(state) {
  const marks = state?.marks || {};
  const at = (name) => Math.max(0, Number(marks?.[name]?.atMs || 0));
  const pickDuration = (endName, startName) => {
    const end = at(endName);
    const start = at(startName);
    if (!end) return 0;
    return Math.max(0, end - start);
  };
  return {
    totalMs: Math.max(0, Number(state?.summary?.totalMs || at("boot-complete") || at("login-ready") || 0)),
    sessionRestoreMs: pickDuration("session-restored", "app-start"),
    versionCheckMs: pickDuration("version-check-finished", "version-check-started"),
    preLoginMs: pickDuration("prelogin-finished", "prelogin-started"),
    globalPullMs: pickDuration("prelogin-global-ready", "prelogin-global-started"),
    catalogsMs: pickDuration("prelogin-catalogs-ready", "prelogin-catalogs-started"),
    shellWarmMs: at("shell-warm-fired") || 0,
    homeWarmMs: at("home-warm-fired") || 0,
    priorityWarmMs: at("priority-warm-fired") || 0,
    adaptiveWarmMs: at("adaptive-warm-fired") || 0,
    loginReadyMs: at("login-ready") || 0,
  };
}

export function finalizeBootTelemetry({ session = null, farm = null, status = "ready", source = "boot", apiSecret = "" } = {}) {
  try {
    const state = ensureBootTelemetryState();
    if (!state || state.finalized) return null;
    setBootTelemetryIdentity(session, farm);
    const completeMark = markBootTelemetry("boot-complete", { status, source });
    const durations = buildBootTelemetryDurations(state);
    const record = {
      boot_id: String(state.bootId || ""),
      account_id: String(state.identity?.accountId || ""),
      user_id: String(state.identity?.userId || ""),
      username: String(state.identity?.username || ""),
      user_name: String(state.identity?.userName || ""),
      role: String(state.identity?.role || ""),
      device_id: getDeviceId(),
      device_label: [state.context?.platform, state.context?.effectiveType || state.context?.connectionType].filter(Boolean).join(" · ") || "Dispositivo",
      app_version: String(state.context?.appVersion || APP_VERSION || ""),
      farm_id: String(state.farm?.id || ""),
      farm_name: String(state.farm?.name || ""),
      is_native_shell: !!state.context?.isNativeShell,
      platform: String(state.context?.platform || ""),
      language: String(state.context?.language || ""),
      status: String(status || "ready"),
      boot_started_at: new Date(Number(state.startedAt || Date.now())).toISOString(),
      boot_finished_at: completeMark?.atIso || new Date().toISOString(),
      total_ms: durations.totalMs,
      session_restore_ms: durations.sessionRestoreMs,
      version_check_ms: durations.versionCheckMs,
      prelogin_ms: durations.preLoginMs,
      global_pull_ms: durations.globalPullMs,
      catalogs_ms: durations.catalogsMs,
      login_ready_ms: durations.loginReadyMs,
      shell_warm_ms: durations.shellWarmMs,
      home_warm_ms: durations.homeWarmMs,
      priority_warm_ms: durations.priorityWarmMs,
      adaptive_warm_ms: durations.adaptiveWarmMs,
      connection_type: String(state.context?.connectionType || ""),
      effective_type: String(state.context?.effectiveType || ""),
      save_data: !!state.context?.saveData,
      downlink_mbps: Number(state.context?.downlink || 0) || 0,
      rtt_ms: Number(state.context?.rtt || 0) || 0,
      visibility_state: String(state.context?.visibilityState || ""),
      phases_json: state.phases || [],
      metadata_json: {
        source,
        route: state.context?.route || "",
        query: state.context?.query || "",
        userAgent: state.context?.userAgent || "",
        deviceMemory: Number(state.context?.deviceMemory || 0) || 0,
        hardwareConcurrency: Number(state.context?.hardwareConcurrency || 0) || 0,
      },
    };
    state.summary = { ...durations, status: record.status, source };
    state.finalized = true;
    if (typeof window !== "undefined") {
      window.__fzWarmDebug = {
        ...(window.__fzWarmDebug || {}),
        bootTelemetry: { summary: state.summary, phases: state.phases, identity: state.identity, farm: state.farm },
      };
    }
    queueBootTelemetryRecord(record);
    return record;
  } catch (e) {
    return null;
  }
}
